import React from 'react';
import { Info, Lightbulb, BookOpen, AlertTriangle, Gauge, Ruler, Zap, Thermometer } from 'lucide-react';

/**
 * Компонент с информацией о нормах и стандартах освещения в России
 */
const InfoContent = () => {
  return (
    <article className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mt-8">
      <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
        <Info className="w-6 h-6 text-blue-500" />
        Нормы и стандарты освещения в России
      </h2>
      
      <div className="prose prose-amber dark:prose-invert max-w-none prose-sm sm:prose-base">
        <h3 className="flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-amber-500" />
          Основные нормативные документы
        </h3>
        <p>
          В России освещение помещений регламентируется следующими основными документами:
        </p>
        <ul>
          <li><strong>СП 52.13330.2016</strong> "Естественное и искусственное освещение" (актуализированная редакция СНиП 23-05-95*)</li>
          <li><strong>СанПиН 1.2.3685-21</strong> "Гигиенические нормативы и требования к обеспечению безопасности и (или) безвредности для человека факторов среды обитания"</li>
          <li><strong>ГОСТ Р 58698-2019</strong> "Освещение искусственное помещений административных зданий"</li>
          <li><strong>СП 256.1325800.2016</strong> "Электроустановки жилых и общественных зданий. Правила проектирования и монтажа"</li>
        </ul>
        <p>
          Эти документы определяют минимальные и рекомендуемые значения освещенности для различных типов помещений, а также другие параметры качества освещения, необходимые для создания безопасной и комфортной световой среды.
        </p>

        <h3 className="flex items-center gap-2 mt-6">
          <Gauge className="w-5 h-5 text-amber-500" />
          Нормы освещенности для разных помещений
        </h3>
        
        <p>Согласно российским стандартам, минимальная необходимая освещенность в люксах (лк) для различных типов помещений:</p>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2 my-4 not-prose">
          <div className="col-span-1">
            <h4 className="font-medium text-gray-900 dark:text-white mb-2">Жилые помещения:</h4>
            <ul className="space-y-1 text-sm">
              <li className="flex justify-between">
                <span>Офисные помещения</span>
                <span className="font-medium">300-500 лк</span>
              </li>
              <li className="flex justify-between">
                <span>Учебные классы</span>
                <span className="font-medium">400-500 лк</span>
              </li>
              <li className="flex justify-between">
                <span>Торговые залы</span>
                <span className="font-medium">300-500 лк</span>
              </li>
              <li className="flex justify-between">
                <span>Читальные залы</span>
                <span className="font-medium">400-500 лк</span>
              </li>
              <li className="flex justify-between">
                <span>Больничные палаты</span>
                <span className="font-medium">200-300 лк</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg border border-amber-100 dark:border-amber-800 my-6">
          <h4 className="text-amber-700 dark:text-amber-300 font-medium flex items-center gap-2 mb-2">
            <AlertTriangle className="w-4 h-4" />
            Важно знать
          </h4>
          <p className="text-amber-600 dark:text-amber-400 text-sm mb-0">
            Нормы являются минимальными требованиями. Для повышения комфорта и производительности рекомендуется превышать минимальные значения на 20-30%, но не более чем на 200% во избежание избыточной яркости и энергопотребления.
          </p>
        </div>

        <h3 className="flex items-center gap-2">
          <Lightbulb className="w-5 h-5 text-amber-500" />
          Параметры качества освещения
        </h3>
        
        <p>
          Помимо уровня освещенности, российские нормативы регламентируют и другие важные параметры освещения:
        </p>
        
        <ol className="space-y-3">
          <li>
            <strong>Равномерность освещения</strong> - отношение минимальной освещенности к средней должно быть не менее 0.7 для зон с постоянным пребыванием людей.
          </li>
          <li>
            <strong>Коэффициент пульсации</strong> - не должен превышать 5% в жилых помещениях, 10% в офисах и 15% в промышленных помещениях.
          </li>
          <li>
            <strong>Показатель дискомфорта (UGR)</strong> - для жилых помещений не более 21, для офисов – не более 19, для точных работ – не более 16.
          </li>
          <li>
            <strong>Индекс цветопередачи (CRI)</strong> - для жилых и общественных помещений рекомендуется использовать источники света с CRI не менее 80.
          </li>
        </ol>

        <h3 className="flex items-center gap-2 mt-6">
          <Thermometer className="w-5 h-5 text-amber-500" />
          Цветовая температура и комфорт
        </h3>
        
        <p>
          Цветовая температура света измеряется в Кельвинах (K) и существенно влияет на восприятие пространства и комфорт. Хотя это не строго регламентируется в СНиП, российские эксперты рекомендуют:
        </p>
        
        <ul>
          <li><strong>2700-3000K (теплый белый)</strong> - для жилых помещений, спален, гостиных, создаёт расслабляющую атмосферу</li>
          <li><strong>3500-4000K (нейтральный белый)</strong> - для кухонь, ванных комнат, коридоров, рабочих кабинетов</li>
          <li><strong>4000-5000K (дневной свет)</strong> - для офисов, учебных заведений, медицинских и торговых помещений</li>
          <li><strong>5000-6500K (холодный белый)</strong> - для производственных помещений, требующих высокой концентрации внимания</li>
        </ul>

        <h3 className="flex items-center gap-2 mt-6">
          <Ruler className="w-5 h-5 text-amber-500" />
          Расчетные методы
        </h3>
        
        <p>
          В России для расчета освещения применяются следующие стандартные методы:
        </p>
        
        <ul>
          <li>
            <strong>Метод коэффициента использования светового потока (Метод средней освещенности)</strong> - основной метод для расчета равномерного общего освещения прямоугольных помещений.
          </li>
          <li>
            <strong>Точечный метод</strong> - используется для расчета локализованного освещения или при сложной геометрии помещения.
          </li>
          <li>
            <strong>Метод удельной мощности</strong> - упрощенный метод для предварительной оценки энергопотребления системы освещения.
          </li>
        </ul>
        
        <p>
          Наш калькулятор использует метод коэффициента использования светового потока, который рекомендован СП 52.13330.2016 и является наиболее распространенным при проектировании освещения.
        </p>

        <h3 className="flex items-center gap-2 mt-6">
          <Zap className="w-5 h-5 text-amber-500" />
          Энергоэффективность освещения
        </h3>
        
        <p>
          В соответствии с Постановлением Правительства РФ №898 от 10.11.2017 и требованиями энергоэффективности, рекомендуются следующие предельные значения удельной мощности освещения:
        </p>
        
        <ul>
          <li>Жилые помещения: 7-9 Вт/м²</li>
          <li>Офисные помещения: 8-12 Вт/м²</li>
          <li>Учебные помещения: 9-12 Вт/м²</li>
          <li>Торговые залы: 12-18 Вт/м²</li>
          <li>Промышленные помещения: в зависимости от сложности работ, 6-20 Вт/м²</li>
        </ul>
        
        <p>
          При применении светодиодных источников света вместо устаревших технологий (люминесцентных ламп, ламп накаливания) удельную мощность можно сократить на 30-70%.
        </p>

        <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-lg border border-blue-100 dark:border-blue-800 my-6">
          <h4 className="text-blue-700 dark:text-blue-300 font-medium mb-2">Рекомендации по энергоэффективности</h4>
          <ul className="text-blue-600 dark:text-blue-400 text-sm space-y-2 mb-0">
            <li>Используйте светодиодные источники света с эффективностью не менее 90 лм/Вт</li>
            <li>Применяйте системы управления освещением (датчики движения, диммеры)</li>
            <li>Учитывайте коэффициенты отражения поверхностей (светлые стены и потолок снижают энергопотребление)</li>
            <li>Планируйте зонирование освещения для экономии энергии</li>
            <li>Выбирайте светильники с минимальными световыми потерями</li>
          </ul>
        </div>

        <p className="font-semibold mt-6">
          Наш калькулятор освещенности поможет вам спроектировать систему освещения, соответствующую российским нормам и стандартам, оптимизировать энергопотребление и обеспечить комфортную световую среду!
        </p>
      </div>
    </article>
  );
};

export default InfoContent;